import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, Chip,
  TextField, MenuItem, Tabs, Tab, List, ListItem, ListItemIcon,
  ListItemText, Divider, InputAdornment
} from '@mui/material';
import {
  Assessment as ReportIcon, Download as DownloadIcon, Print as PrintIcon,
  Description as DocIcon, BarChart as ChartIcon, PieChart as PieIcon,
  TableChart as TableIcon, People as PeopleIcon,
  CurrencyRupee as RupeeIcon, School as SchoolIcon,
  CalendarMonth as CalendarIcon, Summarize as SummarizeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const Reports = () => {
  const [tab, setTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const reportCategories = [
    {
      category: 'Student Reports',
      icon: <PeopleIcon />,
      reports: [
        { name: 'Student List (Class-wise)', description: 'Complete student list with all details by class', type: 'Table', lastGenerated: '2026-02-14' },
        { name: 'Student Strength Report', description: 'Boys/Girls count by class, section, category', type: 'Table', lastGenerated: '2026-02-10' },
        { name: 'Category-wise Report', description: 'SC/ST/OBC/NT/VJNT/OPEN distribution', type: 'Chart', lastGenerated: '2026-02-08' },
        { name: 'New Admissions Report', description: 'List of new admissions for academic year', type: 'Table', lastGenerated: '2026-02-05' },
        { name: 'Student Birthday Report', description: 'Birthdays by month/week', type: 'Table', lastGenerated: '2026-02-01' },
        { name: 'UDISE Report', description: 'UDISE data export for government records', type: 'Document', lastGenerated: '2026-01-15' },
      ],
    },
    {
      category: 'Fee Reports',
      icon: <RupeeIcon />,
      reports: [
        { name: 'Fee Collection Report', description: 'Daily/weekly/monthly fee collection summary', type: 'Chart', lastGenerated: '2026-02-15' },
        { name: 'Fee Defaulters Report', description: 'Students with pending fee balances', type: 'Table', lastGenerated: '2026-02-14' },
        { name: 'Fee Concession Report', description: 'Concessions given by category', type: 'Table', lastGenerated: '2026-02-12' },
        { name: 'Fee Receipt Register', description: 'All fee receipts with details', type: 'Table', lastGenerated: '2026-02-10' },
        { name: 'Scholarship Report', description: 'Scholarship-wise fee adjustment report', type: 'Table', lastGenerated: '2026-02-01' },
      ],
    },
    {
      category: 'Attendance Reports',
      icon: <CalendarIcon />,
      reports: [
        { name: 'Daily Attendance Report', description: 'Class-wise daily attendance summary', type: 'Table', lastGenerated: '2026-02-15' },
        { name: 'Monthly Attendance Report', description: 'Monthly attendance percentage by class', type: 'Chart', lastGenerated: '2026-02-01' },
        { name: 'Absentee Report', description: 'Students absent on specific date', type: 'Table', lastGenerated: '2026-02-15' },
        { name: 'Staff Attendance Report', description: 'Staff attendance summary', type: 'Table', lastGenerated: '2026-02-14' },
      ],
    },
    {
      category: 'Exam Reports',
      icon: <SchoolIcon />,
      reports: [
        { name: 'Result Analysis Report', description: 'Pass/fail analysis by class and subject', type: 'Chart', lastGenerated: '2026-02-08' },
        { name: 'Marksheet Report', description: 'Consolidated marksheets by class', type: 'Document', lastGenerated: '2026-01-30' },
        { name: 'Topper List', description: 'Class-wise toppers and rank list', type: 'Table', lastGenerated: '2026-01-30' },
        { name: 'Subject-wise Analysis', description: 'Average marks and pass rate by subject', type: 'Chart', lastGenerated: '2026-01-28' },
      ],
    },
    {
      category: 'Financial Reports',
      icon: <SummarizeIcon />,
      reports: [
        { name: 'Cashbook Report', description: 'Daily cashbook with receipts and payments', type: 'Table', lastGenerated: '2026-02-15' },
        { name: 'Trial Balance', description: 'Trial balance for accounting year', type: 'Table', lastGenerated: '2026-02-01' },
        { name: 'Ledger Report', description: 'Account-wise ledger entries', type: 'Table', lastGenerated: '2026-02-10' },
        { name: 'Income & Expenditure', description: 'Income vs expenditure summary', type: 'Chart', lastGenerated: '2026-02-01' },
      ],
    },
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Chart': return <ChartIcon fontSize="small" />;
      case 'Table': return <TableIcon fontSize="small" />;
      case 'Document': return <DocIcon fontSize="small" />;
      default: return <ReportIcon fontSize="small" />;
    }
  };

  const filteredCategories = reportCategories.map(cat => ({
    ...cat,
    reports: cat.reports.filter(r =>
      !searchTerm || r.name.toLowerCase().includes(searchTerm.toLowerCase()) || r.description.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  })).filter(cat => tab === 0 ? true : cat.category === reportCategories[tab - 1]?.category);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReportIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
          <Typography variant="h5" fontWeight={700}>Reports Center</Typography>
        </Box>
      </Box>

      <TextField fullWidth placeholder="Search reports..." value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)} sx={{ mb: 3 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }} variant="scrollable">
        <Tab label="All Reports" />
        {reportCategories.map(cat => <Tab key={cat.category} label={cat.category} />)}
      </Tabs>

      {filteredCategories.map(cat => cat.reports.length > 0 && (
        <Card key={cat.category} sx={{ mb: 2 }}>
          <CardContent sx={{ p: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: '#1a9e8f' }}>
              {cat.icon}
              <Typography variant="subtitle2" fontWeight={700}>{cat.category}</Typography>
              <Chip label={`${cat.reports.length} reports`} size="small" sx={{ ml: 'auto' }} />
            </Box>
            <List dense disablePadding>
              {cat.reports.map((report, i) => (
                <React.Fragment key={report.name}>
                  <ListItem sx={{ px: 0 }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>Last: {new Date(report.lastGenerated).toLocaleDateString()}</Typography>
                        <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={() => toast.success(`Downloading ${report.name}`)}>Download</Button>
                        <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={() => toast.info(`Printing ${report.name}`)}>Print</Button>
                      </Box>
                    }>
                    <ListItemIcon sx={{ minWidth: 36 }}>{getTypeIcon(report.type)}</ListItemIcon>
                    <ListItemText
                      primary={<Typography variant="body2" fontWeight={600}>{report.name}</Typography>}
                      secondary={report.description}
                    />
                  </ListItem>
                  {i < cat.reports.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default Reports;
